/**
 * `real_t` storage. An official build defines no `REAL_T_IS_DOUBLE` (`math_defs.h:143-146`),
 * so a `real_t` field, and every Vector, Rect2 and Transform component, holds single precision.
 */
// Bound once, not read from `Math` per call: the core tests run in a vmThreads VM context, where
// each global lookup costs about 160 ns, 30 times the call, and an unwrap makes millions of calls.
const { fround } = Math;
/** The value a `real_t` field holds after `value` is assigned to it. */
export function storedReal(value) {
    return fround(value);
}
/** The vector a `Vector2` field holds after `value` is assigned to it. */
export function storedVector2(value) {
    return { x: storedReal(value.x), y: storedReal(value.y) };
}
/** The vector a `Vector3` field holds after `value` is assigned to it. */
export function storedVector3(value) {
    return { x: storedReal(value.x), y: storedReal(value.y), z: storedReal(value.z) };
}
/**
 * The colour a `Color` field holds after `value` is assigned to it.
 *
 * @canonical A stored Color
 */
export function storedColor(value) {
    return { r: fround(value.r), g: fround(value.g), b: fround(value.b), a: fround(value.a) };
}
/**
 * The transform a `Transform2D` field holds after `value` is assigned to it.
 *
 * @canonical A stored Transform2D
 */
export function storedTransform2D(value) {
    const { a, b, c, d, tx, ty } = value;
    return { a: fround(a), b: fround(b), c: fround(c), d: fround(d), tx: fround(tx), ty: fround(ty) };
}
/** Single precision needs at most nine significant digits to read back unchanged. */
const MAX_REAL_DIGITS = 9;
/**
 * A normal float32 is within half an ulp (6e-8 relative) of its shortest spelling, under half a
 * unit of six digits, so six digits already round to that spelling. A denormal holds fewer bits.
 */
const NORMAL_REAL_MIN = 2 ** -126;
const NORMAL_REAL_FIRST_DIGITS = 6;
/**
 * The shortest decimal text that reads back as the same `real_t`, for a message that names a
 * stored value: `0.99`, not the `0.9900000095367432` a double prints.
 */
export function formatReal(value) {
    if (Number.isNaN(value))
        return 'nan';
    if (!Number.isFinite(value))
        return value > 0 ? 'inf' : '-inf';
    return String(shortestReal(value));
}
/** The number with the fewest significant digits that reads back as the same finite `real_t`. */
export function shortestReal(value) {
    const firstDigits = Math.abs(value) >= NORMAL_REAL_MIN ? NORMAL_REAL_FIRST_DIGITS : 1;
    for (let digits = firstDigits; digits < MAX_REAL_DIGITS; digits++) {
        const shortest = Number(value.toPrecision(digits));
        if (storedReal(shortest) === value)
            return shortest;
    }
    return Number(value.toPrecision(MAX_REAL_DIGITS));
}
//# sourceMappingURL=real.js.map